// // src/steps/ChildStep.tsx
// import React from "react";
// import Input from "../components/ui/Input";
// import Select from "../components/ui/Select";
// import Button from "../components/ui/Button";
// import type { ChildState } from "../wizard/wizardState";
// import { getTshirtSizeOptions } from "../constants/tshirtSizes";
// import { useListManagement } from "../hooks/useListManagement";
// import { fetchGroupAvailability, type AvailableGroup } from "../services/registrationService";

// type Props = {
//   childrenValue: ChildState[];
//   onChange: (next: ChildState[]) => void;
//   errors: Record<string, string>;
// };

// function createBlankChild(): ChildState {
//   return {
//     firstName: "",
//     lastName: "",
//     age: "",
//     grade: "",
//     allergies: "",
//     specialNeeds: "",
//     tshirtSize: "",
//     requestedGroup: ""
//   };
// }

// function getAgeNumber(value: string) {
//   const parsed = Number(value);
//   return Number.isFinite(parsed) ? parsed : null;
// }

// function matchesAgeRange(age: number, ages: string) {
//   const [minAge, maxAge] = ages.split("-").map((value) => Number(value.trim()));
//   if (!Number.isFinite(minAge) || !Number.isFinite(maxAge)) {
//     return false;
//   }

//   return age >= minAge && age <= maxAge;
// }

// function getAvailableGroupsForAge(groups: AvailableGroup[], ageValue: string) {
//   const age = getAgeNumber(ageValue);
//   if (age === null) {
//     return [];
//   }

//   return groups.filter(
//     (group) => group.currentStudents < group.capacity && matchesAgeRange(age, group.ages)
//   );
// }

// export default function ChildStep({ childrenValue, onChange, errors }: Props) {
//   const { updateItem, addItem, removeItem } = useListManagement(childrenValue, {
//     createBlank: createBlankChild
//   });
//   const [availableGroups, setAvailableGroups] = React.useState<AvailableGroup[]>([]);
//   const [isLoadingGroups, setIsLoadingGroups] = React.useState(false);
//   const [groupLoadError, setGroupLoadError] = React.useState<string | null>(null);

//   React.useEffect(() => {
//     let isActive = true;

//     async function loadGroups() {
//       setIsLoadingGroups(true);
//       setGroupLoadError(null);

//       try {
//         const groups = await fetchGroupAvailability();
//         if (isActive) {
//           setAvailableGroups(groups);
//         }
//       } catch (error) {
//         if (isActive) {
//           setGroupLoadError("Unable to load available groups right now.");
//         }
//       } finally {
//         if (isActive) {
//           setIsLoadingGroups(false);
//         }
//       }
//     }

//     loadGroups();

//     return () => {
//       isActive = false;
//     };
//   }, []);

//   React.useEffect(() => {
//     if (availableGroups.length === 0) {
//       return;
//     }

//     const sanitizedChildren = childrenValue.map((child) => {
//       if (!child.requestedGroup) {
//         return child;
//       }

//       const isStillAvailable = getAvailableGroupsForAge(availableGroups, child.age).some(
//         (group) => group.name === child.requestedGroup
//       );

//       return isStillAvailable ? child : { ...child, requestedGroup: "" };
//     });

//     const didChange = sanitizedChildren.some((child, index) => child !== childrenValue[index]);
//     if (didChange) {
//       onChange(sanitizedChildren);
//     }
//   }, [availableGroups, childrenValue, onChange]);

//   const updateChild = (index: number, next: ChildState) => onChange(updateItem(index, next));
//   const addChild = () => onChange(addItem());
//   const removeChild = (index: number) => onChange(removeItem(index));

//   return (
//     <div className="space-y-4">
//       {errors.children ? (
//         <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">
//           {errors.children}
//         </div>
//       ) : null}

//       {childrenValue.map((child, idx) => {
//         const firstNameKey = `child_${idx}_firstName`;
//         const lastNameKey = `child_${idx}_lastName`;
//         const ageKey = `child_${idx}_age`;
//         const gradeKey = `child_${idx}_grade`;
//         const requestedGroupKey = `child_${idx}_requestedGroup`;
//         const tshirtKey = `child_${idx}_tshirtSize`;
//         const hasEnteredAge = child.age.trim().length > 0;
//         const availableGroupsForChild = getAvailableGroupsForAge(availableGroups, child.age);
//         const groupOptions = availableGroupsForChild.map((group) => ({
//           value: group.name,
//           label: `${group.name} (${group.currentStudents}/${group.capacity})`
//         }));

//         return (
//           <div key={idx} className="rounded-xl border border-slate-200 p-3">
//             <div className="mb-3 flex items-center justify-between">
//               <h3 className="text-sm font-bold">Child {idx + 1}</h3>
//               <Button
//                 type="button"
//                 variant="ghost"
//                 onClick={() => removeChild(idx)}
//                 className="px-3 py-2"
//               >
//                 Remove
//               </Button>
//             </div>

//             <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
//               <Input
//                 label="First Name"
//                 value={child.firstName}
//                 onChange={(e) => updateChild(idx, { ...child, firstName: e.target.value })}
//                 error={errors[firstNameKey]}
//               />
//               <Input
//                 label="Last Name"
//                 value={child.lastName}
//                 onChange={(e) => updateChild(idx, { ...child, lastName: e.target.value })}
//                 error={errors[lastNameKey]}
//               />
//             </div>

//             <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
//               <Input
//                 label="Age"
//                 value={child.age}
//                 onChange={(e) => updateChild(idx, { ...child, age: e.target.value })}
//                 error={errors[ageKey]}
//                 inputMode="numeric"
//               />
//               <Input
//                 label="Grade Entering"
//                 value={child.grade}
//                 onChange={(e) => updateChild(idx, { ...child, grade: e.target.value })}
//                 error={errors[gradeKey]}
//                 placeholder="K, 1st, 2nd, etc."
//               />
//             </div>

//             {/* Group selection UI intentionally hidden for now; keep background logic intact. */}

//             <div className="mt-3">
//               <Input
//                 label="Allergies"
//                 value={child.allergies}
//                 onChange={(e) => updateChild(idx, { ...child, allergies: e.target.value })}
//                 placeholder="Comma separated (dairy, nuts, etc.)"
//               />
//             </div>

//             <div className="mt-3">
//               <Input
//                 label="Special Needs"
//                 value={child.specialNeeds}
//                 onChange={(e) => updateChild(idx, { ...child, specialNeeds: e.target.value })}
//                 placeholder="If any"
//               />
//             </div>

//             <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
//               <Select
//                 label="Tshirt Size"
//                 value={child.tshirtSize}
//                 onChange={(e) => updateChild(idx, { ...child, tshirtSize: e.target.value })}
//                 error={errors[tshirtKey]}
//                 placeholder="Select size"
//                 options={getTshirtSizeOptions()}
//               />
//               <p className="-mt-1 text-xs text-slate-600">
//                 Last day to guarantee a shirt size is May 29.
//               </p>
//             </div>
//           </div>
//         );
//       })}

//       <Button type="button" variant="secondary" className="w-full" onClick={addChild}>
//         Add Another Child
//       </Button>
//     </div>
//   );
// }

/////// QUICK AI FIX, ABOVE CODE IS PREVIOUS CODE ////////////////////////////
// src/steps/ChildStep.tsx
import React from "react";
import Input from "../components/ui/Input";
import Select from "../components/ui/Select";
import Button from "../components/ui/Button";
import type { ChildState } from "../wizard/wizardState";
import { getTshirtSizeOptions } from "../constants/tshirtSizes";
import { useListManagement } from "../hooks/useListManagement";
import {
  fetchGroupAvailability,
  type AvailableGroup,
} from "../services/registrationService";

type Props = {
  childrenValue: ChildState[];
  onChange: (next: ChildState[]) => void;
  errors: Record<string, string>;
};

function createBlankChild(): ChildState {
  return {
    firstName: "",
    lastName: "",
    age: "",
    grade: "",
    allergies: "",
    specialNeeds: "",
    tshirtSize: "",
    requestedGroup: "",
  };
}

function getAgeNumber(value: string | null | undefined) {
  const parsed = Number(String(value ?? "").trim());
  return Number.isFinite(parsed) ? parsed : null;
}

function matchesAgeRange(age: number, ages: string | null | undefined) {
  // Prevent crash if ages is null, undefined, or malformed
  if (!ages || typeof ages !== "string") {
    return false;
  }

  const [rawMinAge, rawMaxAge] = ages
    .split("-")
    .map((value) => Number(value.trim()));

  if (!Number.isFinite(rawMinAge) || !Number.isFinite(rawMaxAge)) {
    return false;
  }

  return age >= rawMinAge && age <= rawMaxAge;
}

function getAvailableGroupsForAge(groups: AvailableGroup[], ageValue: string) {
  const age = getAgeNumber(ageValue);

  if (age === null) {
    return [];
  }

  return groups.filter((group) => {
    const hasRoom = group.currentStudents < group.capacity;
    const ageMatches = matchesAgeRange(age, group.ages);

    return hasRoom && ageMatches;
  });
}

export default function ChildStep({ childrenValue, onChange, errors }: Props) {
  const { updateItem, addItem, removeItem } = useListManagement(childrenValue, {
    createBlank: createBlankChild,
  });

  const [availableGroups, setAvailableGroups] = React.useState<AvailableGroup[]>(
    []
  );
  const [isLoadingGroups, setIsLoadingGroups] = React.useState(false);
  const [groupLoadError, setGroupLoadError] = React.useState<string | null>(
    null
  );

  React.useEffect(() => {
    let isActive = true;

    async function loadGroups() {
      setIsLoadingGroups(true);
      setGroupLoadError(null);

      try {
        const groups = await fetchGroupAvailability();

        if (isActive) {
          setAvailableGroups(Array.isArray(groups) ? groups : []);
        }
      } catch (error) {
        if (isActive) {
          setGroupLoadError("Unable to load available groups right now.");
        }
      } finally {
        if (isActive) {
          setIsLoadingGroups(false);
        }
      }
    }

    loadGroups();

    return () => {
      isActive = false;
    };
  }, []);

  React.useEffect(() => {
    if (availableGroups.length === 0) {
      return;
    }

    const sanitizedChildren = childrenValue.map((child) => {
      if (!child.requestedGroup) {
        return child;
      }

      const isStillAvailable = getAvailableGroupsForAge(
        availableGroups,
        child.age
      ).some((group) => group.name === child.requestedGroup);

      return isStillAvailable ? child : { ...child, requestedGroup: "" };
    });

    const didChange = sanitizedChildren.some(
      (child, index) => child !== childrenValue[index]
    );

    if (didChange) {
      onChange(sanitizedChildren);
    }
  }, [availableGroups, childrenValue, onChange]);

  const updateChild = (index: number, next: ChildState) =>
    onChange(updateItem(index, next));

  const addChild = () => onChange(addItem());

  const removeChild = (index: number) => onChange(removeItem(index));

  return (
    <div className="space-y-4">
      {errors.children ? (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          {errors.children}
        </div>
      ) : null}

      {groupLoadError ? (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800">
          {groupLoadError}
        </div>
      ) : null}

      {childrenValue.map((child, idx) => {
        const firstNameKey = `child_${idx}_firstName`;
        const lastNameKey = `child_${idx}_lastName`;
        const ageKey = `child_${idx}_age`;
        const gradeKey = `child_${idx}_grade`;
        const tshirtKey = `child_${idx}_tshirtSize`;

        return (
          <div key={idx} className="rounded-xl border border-slate-200 p-3">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-bold">Child {idx + 1}</h3>

              <Button
                type="button"
                variant="ghost"
                onClick={() => removeChild(idx)}
                className="px-3 py-2"
              >
                Remove
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input
                label="First Name"
                value={child.firstName}
                onChange={(e) =>
                  updateChild(idx, { ...child, firstName: e.target.value })
                }
                error={errors[firstNameKey]}
              />

              <Input
                label="Last Name"
                value={child.lastName}
                onChange={(e) =>
                  updateChild(idx, { ...child, lastName: e.target.value })
                }
                error={errors[lastNameKey]}
              />
            </div>

            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input
                label="Age"
                value={child.age}
                onChange={(e) =>
                  updateChild(idx, { ...child, age: e.target.value })
                }
                error={errors[ageKey]}
                inputMode="numeric"
              />

              <Input
                label="Grade Entering"
                value={child.grade}
                onChange={(e) =>
                  updateChild(idx, { ...child, grade: e.target.value })
                }
                error={errors[gradeKey]}
                placeholder="K, 1st, 2nd, etc."
              />
            </div>

            {/* Group selection UI intentionally hidden for now; background logic stays safe. */}

            <div className="mt-3">
              <Input
                label="Allergies"
                value={child.allergies}
                onChange={(e) =>
                  updateChild(idx, { ...child, allergies: e.target.value })
                }
                placeholder="Comma separated (dairy, nuts, etc.)"
              />
            </div>

            <div className="mt-3">
              <Input
                label="Special Needs"
                value={child.specialNeeds}
                onChange={(e) =>
                  updateChild(idx, { ...child, specialNeeds: e.target.value })
                }
                placeholder="If any"
              />
            </div>

            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Select
                label="Tshirt Size"
                value={child.tshirtSize}
                onChange={(e) =>
                  updateChild(idx, { ...child, tshirtSize: e.target.value })
                }
                error={errors[tshirtKey]}
                placeholder="Select size"
                options={getTshirtSizeOptions()}
              />

              <p className="-mt-1 text-xs text-slate-600">
                Last day to guarantee a shirt size is May 29.
              </p>
            </div>

            {isLoadingGroups ? (
              <p className="mt-3 text-xs text-slate-500">
                Checking group availability...
              </p>
            ) : null}
          </div>
        );
      })}

      <Button
        type="button"
        variant="secondary"
        className="w-full"
        onClick={addChild}
      >
        Add Another Child
      </Button>
    </div>
  );
}